import { AccessRequestApi, GeeksApi, GEEKS_WITH_STATUSES_LIMIT } from '@/api';
import { GeekWithStatus, GeekStatus, PendingUser } from '@/models';
import { Module } from 'vuex';
import { Actions } from './actions';
import { Mutations } from './mutations';
import { RootState } from './root.store';

const accessRequestsApi = new AccessRequestApi();
const geeksApi = new GeeksApi();

export interface GeeksManagementState {
  geeks: GeekWithStatus[];
  accessRequests: PendingUser[];
  pagination: {
    offset: number,
    totalCount: number,
  };
}

export const GeeksManagementModule: Module<GeeksManagementState, RootState> = {
  state: {
    geeks: [],
    accessRequests: [],
    pagination: {
      offset: 0,
      totalCount: 0,
    },
  },

  mutations: {
    [Mutations.management.geeksManagement.setGeeks](state, geeks: GeekWithStatus[]) {
      state.geeks = geeks;
    },
    [Mutations.management.geeksManagement.setAccessRequests](state, requests: PendingUser[]) {
      state.accessRequests = requests;
    },
    [Mutations.management.geeksManagement.setGeeksCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.management.geeksManagement.resetPagination](state) {
      state.pagination.offset = 0;
      state.pagination.totalCount = 0;
    },
    [Mutations.management.geeksManagement.addGeeksWithStatus](state, geeks: GeekWithStatus[]) {
      state.geeks.push(...geeks);
    },
    [Mutations.management.geeksManagement.incrementPagination](state) {
      state.pagination.offset += GEEKS_WITH_STATUSES_LIMIT;
    },
    [Mutations.management.geeksManagement.setGeekStatus](state, { id, status }: { id: string, status: GeekStatus }) {
      state.geeks = state.geeks.map((geek) => geek.id === id ? { ...geek, status } : geek);
    },
  },

  actions: {
    async [Actions.management.geeksManagement.fetchGeeks]({ commit }, filters) {
      commit(Mutations.management.geeksManagement.resetPagination);
      const response = await geeksApi.fetchGeeksWithStatuses(filters);
      commit(Mutations.management.geeksManagement.setGeeks, response.data);
      commit(Mutations.management.geeksManagement.setGeeksCount, response.totalCount);
    },
    async [Actions.management.geeksManagement.fetchAccessRequests]({ commit }) {
      const response = await accessRequestsApi.fetchAccessRequests();
      commit(Mutations.management.geeksManagement.setAccessRequests, response);
    },
    async [Actions.management.geeksManagement.approveAccessRequest](_, userId) {
      return await accessRequestsApi.approveAccessRequest(userId);
    },
    async [Actions.management.geeksManagement.declineAccessRequest](_, userId) {
      return await accessRequestsApi.declineAccessRequest(userId);
    },
    async [Actions.management.geeksManagement.fetchMoreGeeks]({ commit, state }, filters) {
      commit(Mutations.management.geeksManagement.incrementPagination);
      const response = await geeksApi.fetchGeeksWithStatuses(filters, state.pagination.offset);
      commit(Mutations.management.geeksManagement.addGeeksWithStatus, response.data);
      commit(Mutations.management.geeksManagement.setGeeksCount, response.totalCount);
    },
    async [Actions.management.geeksManagement.changeGeeksStatus]({ commit }, { id, status }) {
      if (status === GeekStatus.Active) {
        await geeksApi.activateGeek(id);
      } else if (status === GeekStatus.Inactive) {
        await geeksApi.deactivateGeek(id);
      }

      commit(Mutations.management.geeksManagement.setGeekStatus, { id, status });
    },
  },
};
